import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteConfig } from '@/mocks/config';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    setFormStatus('submitting');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9qq4que3oq4jqumtol0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 哥布林的洞窟',
          description: '了解哥布林的洞窟攻略团队，我们致力于为玩家提供最全面的游戏攻略和深度内容。',
        }}
      />

      <Navbar />

      <main className="pt-16">
        <section className="relative w-full h-[240px] md:h-[320px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Ancient%20underground%20guild%20hall%20with%20large%20round%20table%20surrounded%20by%20stone%20pillars%2C%20adventurers%20gathered%20discussing%20maps%20and%20strategies%2C%20warm%20torchlight%20illuminating%20the%20chamber%2C%20dark%20fantasy%20gathering%20scene%2C%20atmospheric%20with%20amber%20and%20warm%20brown%20tones%2C%20cinematic%20composition&width=1800&height=900&seq=about-hero&orientation=landscape"
              alt="关于我们 - 哥布林的洞窟"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70"></div>
          </div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
              关于我们
            </h1>
            <p className="text-background-50/70 text-sm md:text-base max-w-lg mx-auto">
              一群热爱地下城冒险游戏的玩家，致力于为社区提供最优质的攻略内容
            </p>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <BreadcrumbNav
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  我们的故事
                </h2>
                <div className="space-y-3 text-foreground-600 text-sm leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">哥布林的洞窟攻略站</strong>成立于2024年，由一群热爱地下城冒险类RPG游戏的资深玩家共同创建。我们的创始团队成员来自游戏行业的各个领域——有前游戏策划、职业电竞选手、游戏媒体编辑，以及最核心的——一群在哥布林的洞窟中投入了数千小时的硬核玩家。
                  </p>
                  <p>
                    创立这个网站的初衷很简单：当我们看到新手玩家在洞窟中一次次倒下却找不到像样的中文攻略时，我们决定做点什么。<strong className="text-foreground-900">我们相信每一位冒险者都值得拥有最好的指引。</strong>
                  </p>
                  <p>
                    从最初几篇粗糙的笔记，到如今覆盖六大分类、数十篇深度攻略的完整知识库，我们始终保持着对游戏的热爱和对内容质量的执着。每一篇攻略都经过至少三位编辑的交叉验证，确保信息的准确性和实用性。
                  </p>
                </div>
              </div>
              <div className="h-64 md:h-80 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Team%20of%20fantasy%20adventurers%20sitting%20around%20a%20large%20stone%20table%20in%20a%20guild%20hall%2C%20studying%20maps%20and%20planning%20quests%2C%20warm%20golden%20torchlight%20illuminating%20their%20faces%2C%20diverse%20group%20of%20characters%2C%20dark%20fantasy%20illustration%20with%20rich%20amber%20tones%2C%20cinematic%20atmosphere&width=800&height=600&seq=about-team&orientation=landscape"
                  alt="我们的团队"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
              玩家口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  name: '暗影行者',
                  role: '资深玩家 · 游戏时长800+小时',
                  content: '从新手时期就一直看这个站的攻略，从第一层到击败图尔格，每一篇攻略都写得特别详细。尤其是BOSS攻略的机制拆解，让我少走了太多弯路。强烈推荐给每一位哥布林洞窟的玩家！',
                  rating: 5,
                },
                {
                  name: '月光旅人',
                  role: '攻略作者 · B站UP主',
                  content: '作为内容创作者，我对攻略质量的要求很高。这个网站的文章不仅内容准确，排版和阅读体验也做得很棒。艾拉娜的那篇角色攻略帮我解决了一直困扰的技能搭配问题，现在已经是我的常驻参考站了。',
                  rating: 5,
                },
                {
                  name: '铁壁守护者',
                  role: '战士专精 · 全成就玩家',
                  content: '装备天梯榜那篇文章简直神作！以前我总是纠结装备选择，看完之后豁然开朗。隐藏房间的攻略也帮我找到了好几个之前完全不知道的秘密区域。感谢团队的高质量输出！',
                  rating: 5,
                },
                {
                  name: '洞穴探险家',
                  role: '新手玩家 · 入坑2个月',
                  content: '刚玩游戏的时候完全不知道干什么，朋友推荐了这个站。新手入门指南写得特别友好，一步一步教我怎么玩，现在我已经能独立打到第三层了！地图攻略的标注也很清晰，再也不怕迷路。',
                  rating: 5,
                },
                {
                  name: '星辰守望者',
                  role: '剧情党 · 多周目玩家',
                  content: '世界观解析那篇文章让我对这个游戏有了全新的认识。原来暗月之战背后还有这么深的剧情！三大结局的触发条件整理得也很清楚，帮我成功解锁了隐藏结局。网站的内容深度确实没得说。',
                  rating: 5,
                },
                {
                  name: '兽人格拉克',
                  role: '竞速玩家 · 速通榜前十',
                  content: '作为一个追求效率的速通玩家，这里的攻略帮我优化了很多路线。NPC位置整理和锻造配方的汇总特别实用，减少了大量试错时间。希望未来能看到更多关于速通技巧的深度内容！',
                  rating: 4,
                },
              ].map((review, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <i
                        key={j}
                        className={`text-sm ${j < review.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div>
                    <p className="text-foreground-900 text-sm font-semibold">{review.name}</p>
                    <p className="text-foreground-400 text-xs">{review.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm leading-relaxed mb-6">
                  无论你是想投稿攻略、报告错误、提出建议还是商务合作，都欢迎通过以下方式与我们取得联系。我们会在1-3个工作日内回复。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">电子邮箱</h4>
                      <p className="text-sm text-foreground-500">{siteConfig.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-bilibili-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">B站主页</h4>
                      <a href={siteConfig.social.bilibili} target="_blank" rel="nofollow noopener" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                        访问我们的B站空间 <i className="ri-external-link-line text-xs"></i>
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-weibo-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">微博</h4>
                      <a href={siteConfig.social.weibo} target="_blank" rel="nofollow noopener" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                        关注我们的微博 <i className="ri-external-link-line text-xs"></i>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">我们的使命</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { icon: 'ri-book-open-line', label: '深度攻略', desc: '每篇都是精品' },
                      { icon: 'ri-team-line', label: '社区驱动', desc: '玩家共建共享' },
                      { icon: 'ri-shield-check-line', label: '持续更新', desc: '紧跟版本变化' },
                    ].map((item, i) => (
                      <div key={i} className="text-center p-3 rounded-lg bg-background-100 border border-background-200/70">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-2">
                          <i className={`${item.icon} text-primary-600`}></i>
                        </div>
                        <p className="text-xs font-semibold text-foreground-900">{item.label}</p>
                        <p className="text-xs text-foreground-400 mt-0.5">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  给我们留言
                </h2>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6 space-y-4"
                >
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      你的名字 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="about-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入你的名字"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="about-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="about-subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                    >
                      <option value="">请选择留言主题</option>
                      <option value="攻略投稿">攻略投稿</option>
                      <option value="内容建议">内容建议</option>
                      <option value="错误反馈">错误反馈</option>
                      <option value="商务合作">商务合作</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入你的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  <div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                  </div>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-md bg-accent-100 text-accent-700 text-sm flex items-center gap-2">
                      <i className="ri-checkbox-circle-line"></i>
                      {formMessage}
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 rounded-md bg-red-50 text-red-600 text-sm flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      {formMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>发送中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>发送留言
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}